import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import patientInfoService from '../patientInfo/patientInfoService';
import './AllPatients.css';

const GENERATED_EMAIL_DOMAIN = '@newupt.com';

const AllPatients = () => {
  const [patients, setPatients] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const patientsPerPage = 10;

  const navigate = useNavigate();
  const service = new patientInfoService();

  const rawRole = localStorage.getItem('role') || '';
  const normalizedRole = rawRole.replace('ROLE_', '');

  const isManagerOrAdmin =
    normalizedRole === 'ADMIN' || normalizedRole === 'MANAGER';

  const isGeneratedNewUEmail = (email) => {
    return String(email || '')
      .trim()
      .toLowerCase()
      .endsWith(GENERATED_EMAIL_DOMAIN);
  };

  const canStaffModifyPatient = (patient) => {
    return isManagerOrAdmin && isGeneratedNewUEmail(patient?.email);
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);

    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3500);
  };

  const fetchPatients = async () => {
    try {
      const allPatients = await service.getAllPatients();
      setPatients(allPatients || []);
      setFilteredPatients(allPatients || []);
    } catch (err) {
      console.error('Error loading patients:', err.message);
      showMessage(`Error loading patients: ${err.message}`, 'error');
    }
  };

  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const input = searchInput.toLowerCase().trim();

    const filtered = patients.filter((p) => {
      const fullName = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      const phone = `${p.phoneNumber || ''}`.toLowerCase();
      const email = `${p.email || ''}`.toLowerCase();

      return (
        fullName.includes(input) ||
        phone.includes(input) ||
        email.includes(input)
      );
    });

    setFilteredPatients(filtered);
    setCurrentPage(1);
  }, [searchInput, patients]);

  const handleClick = (patientId) => {
    if (!patientId) return;

    localStorage.setItem('patientId', patientId);
    navigate('/dashboard/patient');
  };

  const handleViewPatient = (e, patientId) => {
    e.stopPropagation();
    handleClick(patientId);
  };

  const callDeletePatient = async (patientId) => {
    if (typeof service.deletePatientById === 'function') {
      return service.deletePatientById(patientId);
    }

    if (typeof service.deletePatient === 'function') {
      return service.deletePatient(patientId);
    }

    if (typeof service.delete === 'function') {
      return service.delete(patientId);
    }

    throw new Error(
      'No delete method found in patientInfoService. Send patientInfoService.js so I can add the correct delete method.'
    );
  };

  const handleDeletePatient = async (e, patient) => {
    e.stopPropagation();

    if (!canStaffModifyPatient(patient)) {
      alert(
        'This patient has a real email on file. Staff cannot delete this patient.'
      );
      return;
    }

    const patientName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
    const confirmDelete = window.confirm(
      `Delete ${patientName || 'this patient'}?\n\nOnly staff-created patients with ${GENERATED_EMAIL_DOMAIN} emails can be deleted. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await callDeletePatient(patient.patientId);

      const updatedPatients = patients.filter(
        (p) => p.patientId !== patient.patientId
      );

      setPatients(updatedPatients);
      setFilteredPatients((prev) =>
        prev.filter((p) => p.patientId !== patient.patientId)
      );

      showMessage('Patient deleted successfully.', 'success');
    } catch (err) {
      console.error('Error deleting patient:', err);
      showMessage(`Delete failed: ${err.message}`, 'error');
    }
  };

  const indexOfLast = currentPage * patientsPerPage;
  const indexOfFirst = indexOfLast - patientsPerPage;
  const currentPatients = filteredPatients.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredPatients.length / patientsPerPage);

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="allPatientsWrapper">
      <div className="patientCard">
        <h2>All Patients</h2>

        {message && (
          <div
            className={
              messageType === 'error'
                ? 'patientMessage patientMessageError'
                : 'patientMessage patientMessageSuccess'
            }
          >
            {message}
          </div>
        )}

        <input
          type="text"
          placeholder="Search patients by name, phone, or email..."
          className="searchInput"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <div className="patientCount">
          Showing {currentPatients.length} of {filteredPatients.length} patients
        </div>

        {isManagerOrAdmin && (
          <div className="patientStaffNote">
            Staff can delete only generated patient accounts ending in{' '}
            <strong>{GENERATED_EMAIL_DOMAIN}</strong>. Patients with real emails
            are locked from staff deletion.
          </div>
        )}

        <div className="tableContainer">
          <table className="patientsTable">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Date of Birth</th>
                <th>Sex</th>
                <th>Phone</th>
                <th>Email</th>
                {isManagerOrAdmin && <th>Actions</th>}
              </tr>
            </thead>

            <tbody>
              {currentPatients.map((patient) => {
                const canModify = canStaffModifyPatient(patient);

                return (
                  <tr
                    key={patient.patientId}
                    className="patientRow"
                    onClick={() => handleClick(patient.patientId)}
                  >
                    <td>{patient.firstName}</td>
                    <td>{patient.lastName}</td>
                    <td>{patient.dateOfBirth}</td>
                    <td>{patient.sex}</td>
                    <td>{patient.phoneNumber}</td>
                    <td>
                      <div className="patientEmailCell">
                        <span>{patient.email}</span>

                        {isManagerOrAdmin && (
                          <span
                            className={
                              canModify
                                ? 'emailStatusGenerated'
                                : 'emailStatusLocked'
                            }
                          >
                            {canModify ? 'Staff-created' : 'Locked'}
                          </span>
                        )}
                      </div>
                    </td>

                    {isManagerOrAdmin && (
                      <td>
                        <div className="patientActions">
                          <button
                            type="button"
                            className="patientActionButton viewButton"
                            onClick={(e) =>
                              handleViewPatient(e, patient.patientId)
                            }
                          >
                            View
                          </button>

                          <button
                            type="button"
                            className={
                              canModify
                                ? 'patientActionButton deleteButton'
                                : 'patientActionButton deleteButton disabledButton'
                            }
                            disabled={!canModify}
                            title={
                              canModify
                                ? 'Delete staff-created patient'
                                : 'Cannot delete because this patient has a real email'
                            }
                            onClick={(e) => handleDeletePatient(e, patient)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}

              {currentPatients.length === 0 && (
                <tr>
                  <td
                    colSpan={isManagerOrAdmin ? '7' : '6'}
                    className="noResults"
                  >
                    No patients found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination simplePagination">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <span className="pageInfo">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllPatients;